import React, {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'next-i18next';
import {
	Button,
	FormControl,
	FormErrorMessage,
	FormLabel,
	Input,
} from '@tempo/core';
import {useToast} from '@deezer/next/shared/hooks/useToast';
import type {SubLabel, LabelFormData} from '../../types';
import type {
	LabelSearchResult,
	ProviderSearchResult,
} from '@deezer/next/services/api/Starmania/requests/search/types';
import LabelSearchInput from '../../components/LabelSearchInput';
import ProviderSearchInput from '@deezer/next/features/ClaimAccess/components/ProviderSearchInput';
import UPCInfoTooltip from '../../components/UPCInfoTooltip';
import StarmaniaApi from '@deezer/next/services/api/Starmania';
import {ApiErrorCode} from '@constants/errorCodes';
import {isErrorCode} from '@deezer/next/services/api/utils';
import {ArrowRightIcon} from '@tempo/icons';
import TitleRow from '@components/PageElements/PageHeader/TitleRow';
import FooterRow from '@components/PageElements/FooterRow';
import PageLayout from '@deezer/next/shared/layouts/PageLayout';
import FormSectionTitle from '@components/PageElements/FormSectionTitle';
import FormContainer from '@components/PageElements/FormContainer';
import FormSection from '@components/PageElements/FormSection';
import FormSectionContainer from '@components/PageElements/FormSectionContainer';

type AddEditLabelFormCardProps = {
	formData: LabelFormData;
	selectedLabel?: SubLabel;
	onBack: () => void;
	onSave: (label: SubLabel, isEdition: boolean) => void;
	edit: boolean;
};

const AddEditLabelFormCard = ({
	formData,
	selectedLabel,
	onBack,
	onSave,
	edit,
}: AddEditLabelFormCardProps) => {
	const {t} = useTranslation('d4c');
	const selectedLabelIds = useMemo(
		() => formData.labelSubLabels.map(({id}) => id),
		[formData.labelSubLabels],
	);
	const [selectedProvider, setSelectedProvider] = useState<
		ProviderSearchResult | undefined
	>(
		selectedLabel
			? {id: selectedLabel.providerId, name: selectedLabel.providerName}
			: undefined,
	);
	const [upc, setUPC] = useState(selectedLabel?.upc || '');
	const [upcError, setUPCError] = useState<string>();
	const [providerError, setProviderError] = useState<string>();
	const [label, setLabel] = useState<LabelSearchResult | undefined>(
		selectedLabel,
	);
	const selectedProviderIds = useMemo(() => {
		if (!label) {
			return [];
		}
		return formData.labelSubLabels
			.filter(
				({name, providerId}) =>
					name === label.name &&
					(!selectedProvider || selectedProvider.id !== providerId) &&
					(!selectedLabel || selectedLabel.providerId !== providerId),
			)
			.map(({providerId}) => providerId);
	}, [formData.labelSubLabels, label, selectedLabel, selectedProvider]);
	const toast = useToast();

	const handleUPCChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setUPC(e.target.value);
		setUPCError(undefined);
	};

	const handleClearUPCOnRemoveItem = useCallback(() => {
		setUPC('');
	}, []);

	const handleSelectedLabelChange = useCallback((item?: LabelSearchResult) => {
		setUPC('');
		setUPCError(undefined);
		setLabel(item);
	}, []);

	const handleSelectedProviderChange = useCallback(
		(item?: ProviderSearchResult) => {
			if (!item) return;
			setUPCError(undefined);
			setSelectedProvider(item);
		},
		[],
	);

	const handleSave = async () => {
		if (!label || !selectedProvider) return;

		try {
			const {data} = await StarmaniaApi.album.verifyAlbumUpcForLabel(
				upc,
				label.name,
				label.id,
				selectedProvider.id,
			);

			// Ensure there is no duplicate label / provider combination
			if (
				formData.labelSubLabels.some(
					({id, providerId}) =>
						id !== label.id &&
						id === data.id &&
						providerId === selectedProvider.id,
				)
			) {
				setProviderError(
					t('errormessage_text_labelprovidercouplealreadylisted_d4c-web', {
						sprintf: [label.name, selectedProvider.name],
					}),
				);
				return;
			}

			onSave(
				{
					...label,
					id: data.id,
					upc,
					providerName: selectedProvider.name,
					providerId: selectedProvider.id,
				},
				edit,
			);
		} catch (e: any) {
			const errorCode = Number(e.response.data.errors?.[0]?.code);
			if (
				isErrorCode(errorCode, [
					ApiErrorCode.INVALID_UPC,
					ApiErrorCode.NOT_FOUND_UPC,
				])
			) {
				setUPCError(
					t('errormessage_text_upcinvalidornomatch_d4c-web', {
						sprintf: [label.name, selectedProvider.name],
					}),
				);
			} else if (isErrorCode(errorCode, ApiErrorCode.PROVIDER_NEEDED)) {
				setProviderError(t('errormessage_text_providerneeded_d4c-web'));
			} else {
				toast({
					title: t('errormessage_title_somethingwentwrong_d4c-web'),
					description: t('toastmessage_text_unknownerroroccurred_d4c-web'),
					status: 'error',
				});
			}
		}
	};

	return (
		<PageLayout
			data-testid={`${edit ? 'edit' : 'add'}-label-card`}
			header={<TitleRow title={t('requestaccess_title_newrequest_d4c-web')} />}
			footer={
				<FooterRow
					rightElements={
						<>
							<Button
								data-testid="back-button"
								variant="outline"
								onClick={onBack}
								leftIcon={<ArrowRightIcon transform="scaleX(-1)" />} // todo fix that
								size="small"
							>
								{t('generic_action_back_d4c-web')}
							</Button>
							<Button
								data-testid="save-label-button"
								isDisabled={
									!label ||
									!label.id ||
									!selectedProvider ||
									!upc ||
									Boolean(upcError)
								}
								onClick={handleSave}
								size="small"
							>
								{t('generic_action_save_d4c-web')}
							</Button>
						</>
					}
				/>
			}
		>
			<FormContainer>
				<FormSection>
					<FormSectionTitle
						title={
							edit
								? t('requestaccesslabel_title_editlabel_d4c-web')
								: t('requestaccesslabel_title_addnewlabel_d4c-web')
						}
					/>
					<FormSectionContainer>
						<FormControl isRequired={true} isInvalid={Boolean(providerError)}>
							<FormLabel htmlFor="provider-search-name">
								{t('requestaccessprovider_text_providername_d4c-web')}
							</FormLabel>
							<ProviderSearchInput
								data-testid="claim-new-provider-search"
								id="provider-name-search"
								placeholder={t('pitchingtool_text_enterprovidername_d4c-web')}
								selectedProvider={selectedProvider}
								onSelectedProviderChange={handleSelectedProviderChange}
								filterProviderIds={selectedProviderIds}
							/>
							<FormErrorMessage>{providerError}</FormErrorMessage>
						</FormControl>
						<FormControl
							isRequired={true}
							isDisabled={!selectedProvider || !selectedProvider.id}
						>
							<FormLabel htmlFor="label-name-search">
								{t('requestaccesslabel_text_labelname_d4c-web')}
							</FormLabel>
							<LabelSearchInput
								data-testid="claim-new-label-search"
								id="label-name-search"
								placeholder={t(
									'requestaccesslabel_text_enterlabelname_d4c-web',
								)}
								selectedLabel={label}
								selectedProviderId={selectedProvider?.id}
								onSelectedLabelChange={handleSelectedLabelChange}
								isDisabled={edit || !selectedProvider || !selectedProvider.id}
								handleClearUPCOnRemoveItem={handleClearUPCOnRemoveItem}
								filterLabelIds={selectedLabelIds}
							/>
						</FormControl>
						<FormControl
							isRequired={true}
							isDisabled={!label}
							isInvalid={Boolean(upcError)}
						>
							<FormLabel htmlFor="upc">
								{t('deliverymonitoring_text_upc_d4c-web')}
								<UPCInfoTooltip top="-1px" ms={4} position="absolute" />
							</FormLabel>
							<Input
								data-testid="claim-new-label-upc"
								id="upc"
								type="text"
								value={upc}
								onChange={handleUPCChange}
							/>
							<FormErrorMessage>{upcError}</FormErrorMessage>
						</FormControl>
					</FormSectionContainer>
				</FormSection>
			</FormContainer>
		</PageLayout>
	);
};

export default AddEditLabelFormCard;
